import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Users, Clock, MapPin, CheckCircle, User } from 'lucide-react';
import api from 'services/api';
import { DateRangeFilterBlur } from 'components/filters';

const EscalaTecnicos = () => {
  const [loading, setLoading] = useState(true);
  const [escala, setEscala] = useState([]);
  const [resumo, setResumo] = useState({});
  const today = new Date();
  const nextWeek = new Date(today);
  nextWeek.setDate(today.getDate() + 7);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ 
    presetId: 'custom', 
    start: toISO(today), 
    end: toISO(nextWeek) 
  });

  const carregarEscala = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/operacional/escala-tecnicos', {
        params: {
          data_inicio: range.start,
          data_fim: range.end
        }
      });

      if (response.data?.success) {
        setEscala(response.data.data.escala || []);
        setResumo(response.data.data.resumo || {});
      } else {
        // Fallback para estrutura alternativa
        setEscala(response.data?.data || []);
        setResumo(response.data?.resumo || {});
      }
    } catch (error) {
      console.error('❌ Erro ao carregar escala:', error);
      setEscala([]);
      setResumo({});
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end]);

  useEffect(() => {
    carregarEscala();
  }, [carregarEscala]);

  return (
    <div className="space-y-6">
      {/* Header e Filtros */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[color:var(--text-primary)] flex items-center">
            <Users className="h-5 w-5 mr-2 text-[color:var(--accent)]" />
            Escala de Técnicos
          </h3>
          <p className="text-sm text-[color:var(--text-muted)]">Visualize a escala atual e futura dos técnicos de campo</p>
        </div>
        <DateRangeFilterBlur
          value={range}
          onChange={(newRange) => {
            setRange(newRange);
          }}
        />
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--accent)]">Técnicos Ativos</p>
              <p className="text-2xl font-bold text-[color:var(--accent)]">{resumo.total_tecnicos || 0}</p>
            </div>
            <Users className="h-8 w-8 text-[color:var(--accent)]" />
          </div>
        </div>
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--status-good)]">Agendamentos</p>
              <p className="text-2xl font-bold text-[color:var(--status-good)]">{resumo.total_agendamentos || 0}</p>
            </div>
            <Calendar className="h-8 w-8 text-[color:var(--status-good)]" />
          </div>
        </div>
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--status-warn)]">Horas Planejadas</p>
              <p className="text-2xl font-bold text-[color:var(--status-warn)]">{resumo.total_horas || 0}h</p>
            </div>
            <Clock className="h-8 w-8 text-[color:var(--status-warn)]" />
          </div>
        </div>
        <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[color:var(--status-warn)]">Taxa Ocupação</p>
              <p className="text-2xl font-bold text-[color:var(--status-warn)]">{resumo.taxa_ocupacao || 0}%</p>
            </div>
            <CheckCircle className="h-8 w-8 text-[color:var(--status-warn)]" />
          </div>
        </div>
      </div>

      {/* Tabela de Escala */}
      <div className="bg-[color:var(--surface)] rounded-lg border border-[color:var(--border)] shadow-sm">
        <div className="p-4 border-b border-[color:var(--border)]">
          <h4 className="text-lg font-semibold text-[color:var(--text-primary)]">Escala de Agendamentos</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[color:var(--border)]">
            <thead className="bg-[color:var(--surface-muted)]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                  Técnico
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                  Região
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                  Horário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                  OS
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[color:var(--text-muted)] uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-[color:var(--surface)] divide-y divide-[color:var(--border)]">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[color:var(--border)] border-t-[color:var(--accent)]"></div>
                      <span className="ml-2 text-[color:var(--text-muted)]">Carregando escala...</span>
                    </div>
                  </td>
                </tr>
              ) : escala.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-[color:var(--text-muted)]">
                    Nenhuma escala encontrada para o período selecionado
                  </td>
                </tr>
              ) : (
                escala.map((item, index) => (
                  <tr key={index} className="hover:bg-[color:var(--surface-muted)]">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="h-5 w-5 text-[color:var(--text-muted)] mr-2" />
                        <div>
                          <div className="text-sm font-medium text-[color:var(--text-primary)]">{item.tecnico_nome || item.tecnico || 'N/A'}</div>
                          <div className="text-sm text-[color:var(--text-muted)]">{item.especialidade || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-[color:var(--text-muted)] mr-1" />
                        <span className="text-sm text-[color:var(--text-primary)]">{item.regiao || 'N/A'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {item.data_agendamento ? new Date(item.data_agendamento).toLocaleDateString('pt-BR') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[color:var(--text-primary)]">
                      {item.hora_agendamento || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-[color:var(--surface-muted)] text-[color:var(--accent)] rounded-full">
                        {item.os_id || item.os || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'Agendado' ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-warn)]' :
                        item.status === 'Em Andamento' ? 'bg-[color:var(--surface-muted)] text-[color:var(--accent)]' :
                        item.status === 'Concluído' ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)]' :
                        'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]'
                      }`}>
                        {item.status || 'Agendado'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EscalaTecnicos;
